/* eslint-disable no-unused-vars */
/* eslint-disable no-console */
import React, { useReducer } from 'react';

import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Moment from 'moment';
import { extendMoment } from 'moment-range';
import ReactTooltip from 'react-tooltip';
import { ImInfo } from 'react-icons/im';
import { isEmpty } from 'lodash';
import Button from '../../components/button/button';
import InstallmentContainer from '../../components/installmentcontainer/installmentcontainer';
import HeaderTable from '../../components/headertable/headertable';

import { QUESTIONS, WARNINGS } from '../../constants.jsx';
import { StoreContext } from '../../utils/store';
import { resize } from '../../utils/calculate';

const moment = extendMoment(Moment);

const Question2 = props => {
  const {
    onFinish,
    onBack,
    question2HelpText,
    installments: [installments, setInstallments],
    incurredDate: [incurredDate],
    incurredAmount: [incurredAmount],
    finalPaymentDate: [finalDate],
  } = { ...props };

  const {
    installmentRangeFirstDate: [
      installmentRangeFirstDate,
      setInstallmentRangeFirstDate,
    ],
    installmentRangeLastDate: [
      installmentRangeLastDate,
      setInstallmentRangeLastDate,
    ],
    installmentRangeAmount: [installmentRangeAmount, setInstallmentRangeAmount],
    installmentRangeFrequency: [
      installmentRangeFrequency,
      setInstallmentRangeFrequency,
    ],
  } = React.useContext(StoreContext);

  const [validationErrors, setValidationErrors] = React.useState([]);
  const [validationWarning, setValidationWarning] = React.useState();
  const [maximumWarning, setMaximumWarning] = React.useState(false);

  const [_, forceUpdate] = useReducer(x => x + 1, 0);

  const hasErrors = validationErrors && validationErrors.length > 0;

  const getNextKey = list => {
    if (isEmpty(list)) {
      return 0;
    }
    const keys = Object.values(list).map(n => {
      return parseInt(n.key, 10);
    });
    return Math.max(...keys) + 1;
  };

  const updateWarning = () => {
    const errors = validationErrors;
    if (maximumWarning) {
      errors.push('MAXIMUM');
    }
    if (errors && errors.length > 0) {
      const warning = [];
      WARNINGS.forEach(item => {
        if (errors.includes(item.name)) {
          let string = '';
          if (item.name === 'MAXIMUM') {
            string = item.value.start;
            string += maximumWarning;
            string += item.value.end;
          } else {
            string = item.value;
          }
          warning.push(string);
        }
      });
      setValidationWarning(warning);
    } else {
      setValidationWarning(undefined);
    }
  };

  React.useEffect(() => {
    updateWarning();
  }, [validationErrors]);

  React.useEffect(() => {
    updateWarning();
  }, [maximumWarning]);

  React.useEffect(() => {
    resize();
  }, [installments, validationErrors]);

  const updateInstallments = inst => {
    const newInsts = inst;
    // Sort the installments by date
    newInsts.sort(function compare(a, b) {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateA - dateB;
    });
    let i = 0;
    // Set new sort order
    newInsts.forEach((item, index) => {
      newInsts[index].sort = i;
      i += 1;
    });
    setInstallments(newInsts);
  };

  const datesAreOnSameDay = (first, second) => {
    // Is first and second date the same
    return (
      first.getFullYear() === second.getFullYear() &&
      first.getMonth() === second.getMonth() &&
      first.getDate() === second.getDate()
    );
  };

  const validateAllInstallments = () => {
    // Validate all installments before continuting
    let valid = true;
    const dates = [];
    const newInstallments = installments;
    const incurredAmountFlt = parseFloat(incurredAmount);
    const errors = [];

    newInstallments.forEach((item, index) => {
      const amountFlt = parseFloat(item.amount);
      newInstallments[index].dateErrorMessage = '';
      newInstallments[index].amountErrorMessage = '';

      if (item.date) {
        dates.forEach(d => {
          if (datesAreOnSameDay(d, item.date)) {
            newInstallments[index].dateErrorMessage =
              'Duplicate date - please combine payment entries for the same date';
            valid = false;
            errors.push('DUPLICATE');
          }
        });
        dates.push(item.date);
      }
      newInstallments[index].errorMessage = '';
      if (item.date === undefined || item.date === null) {
        newInstallments[index].dateErrorMessage = 'A date must be set';
        valid = false;
        errors.push('INVALID');
      } else if (item.date < incurredDate) {
        newInstallments[index].dateErrorMessage =
          'Date cannot be before incurred date';
        valid = false;
        errors.push('INVALID');
      }
      if (item.amount === undefined) {
        newInstallments[index].amountErrorMessage = 'An amount must be set';
        valid = false;
        errors.push('INVALID');
      }
      if (item.amount === '0.00' || item.amount === 0) {
        newInstallments[index].amountErrorMessage =
          'Amount must be greater than 0';
        valid = false;
        errors.push('INVALID');
      }
    });
    setValidationErrors(errors);
    setInstallments(newInstallments);
    return valid;
  };

  // Calculate the installment schedual
  const addInstallmentRange = () => {
    const newInstallments = installments;
    const range = moment.range(
      moment(installmentRangeFirstDate),
      moment(installmentRangeLastDate)
    );
    let frequency = installmentRangeFrequency;
    let maxInstallments = 200;
    let installmentCount = 1;
    const isFortnight = installmentRangeFrequency === 'fortnight';
    if (isFortnight) {
      frequency = 'week';
      maxInstallments *= 2;
    }
    let max = false;

    // eslint-disable-next-line no-restricted-syntax
    for (const date of range.by(frequency)) {
      if (installmentCount > maxInstallments) {
        // console.log('Hit max installments');
        let lastDate = date.subtract(1, frequency);
        if (isFortnight) {
          lastDate = lastDate.subtract(1, frequency);
        }
        max = lastDate.format('DD/MM/YYYY');
        break;
      }
      if (isFortnight && installmentCount % 2 === 0) {
        installmentCount += 1;
        // console.log('Skipping week ' + installmentCount);

        // eslint-disable-next-line no-continue
        continue;
      }
      const key = getNextKey(newInstallments);
      newInstallments.push({
        id: key,
        key,
        date: date.toDate(),
        amount: installmentRangeAmount,
        dateErrorMessage: undefined,
        amountErrorMessage: undefined,
        sort: key,
      });
      // console.log(date);
      installmentCount += 1;
    }
    // console.log(newInstallments);
    updateInstallments(newInstallments);
    validateAllInstallments();
    setMaximumWarning(max);
    forceUpdate();
  };

  return (
    <>
      <h3>
        <span>
          {QUESTIONS.QUESTION2_TITLE}
          {question2HelpText.length ? (
            <a data-event="click" type="submit" data-tip data-for="tooltip1">
              <ImInfo className="help-text-button" />
            </a>
          ) : null}
        </span>
      </h3>

      <HeaderTable
        startDate={incurredDate}
        finalDate={finalDate}
        amount={incurredAmount}
      />
      {question2HelpText.length ? (
        <ReactTooltip
          globalEventOn="click"
          globalEventOff="click"
          id="tooltip1"
          place="bottom"
          effect="solid"
        >
          <div>
            <span>{question2HelpText}</span>
          </div>
        </ReactTooltip>
      ) : null}
      <br />
      <p>{QUESTIONS.QUESTION3} </p>
      <InstallmentContainer
        title="Installments"
        installments={installments}
        setInstallments={setInstallments}
        addInstallmentRange={addInstallmentRange}
        getNextKey={getNextKey}
        finalDate={finalDate}
        hasErrors={hasErrors}
        setValidationErrors={setValidationErrors}
        validationWarning={validationWarning}
        setMaximumWarning={setMaximumWarning}
      />
      <Row className="button-wrapper">
        <Col className="no-margin">
          <Button title="Back" variant="secondary" onClick={onBack} />
          <Button
            title="Next"
            variant="primary"
            onClick={() => {
              if (validateAllInstallments()) {
                onFinish({ installments });
              }
              forceUpdate();
            }}
          />
        </Col>
      </Row>
    </>
  );
};

export default Question2;
